package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

val LProjectMarkStyle = CssStyle.base {
    Modifier
        .width(ControlHeight.MD)
        .height(ControlHeight.MD)
        .borderRadius(Radius.Control)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        .displayFont()
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("flex", "0 0 auto")
            property("line-height", "1")
            property("text-transform", "none")
        }
}

/**
 * The box an uploaded project icon renders in, sized to match [LProjectMarkStyle] so a custom mark
 * and a preset mark occupy the same footprint at every call site that does not ask for a different
 * size.
 *
 * Without this the `Img` branch of `ProjectIcon` carried only the caller's own modifier, and two of
 * its three call sites passed none: the uploaded image rendered at its raw pixel size — sometimes
 * a multi-hundred-pixel photo filling a card meant for a 40px mark. `object-fit: cover` keeps a
 * non-square upload from distorting inside the fixed box, and the surface background gives a
 * transparent PNG a ground instead of showing whatever sits behind it.
 */
val LProjectImageStyle = CssStyle.base {
    Modifier
        .width(ControlHeight.MD)
        .height(ControlHeight.MD)
        .borderRadius(Radius.Control)
        .backgroundColor(colorMode.toSitePalette().surface)
        .styleModifier {
            property("object-fit", "cover")
            property("flex", "0 0 auto")
            property("display", "block")
        }
}

/**
 * A project identity rendered from the stable shared icon/accent keys.
 *
 * Project colour is deliberately confined to this mark and comes from the contrast-tested identity
 * palette, never from product action or status semantics. A monogram is text, never a substitute
 * Unicode icon; an empty name uses Lucide's product-neutral folder data.
 */
@Composable
fun LProjectMark(
    name: String,
    iconKey: ProjectIconKey,
    accentKey: ProjectAccentKey,
    modifier: Modifier = Modifier,
) {
    val palette = ColorMode.current.toSitePalette()
    val tone = projectIdentityTone(accentKey, ColorMode.current.toProjectIdentityPalette())
    val monogram = projectMonogram(name)
    Span(
        LProjectMarkStyle.toModifier()
            .backgroundColor(tone.tintOn(palette.surface, 0.12f))
            .color(tone)
            .border(1.px, LineStyle.Solid, tone)
            .then(modifier)
            .toAttrs {
                attr("data-project-mark", "")
                attr("data-icon-key", iconKey.name)
                attr("data-accent-key", accentKey.name)
                attr("aria-hidden", "true")
            },
    ) {
        if (iconKey == ProjectIconKey.MONOGRAM && monogram != null) {
            Span(attrs = {
                attr("data-project-monogram", "")
                attr("dir", "auto")
            }) {
                Text(monogram)
            }
        } else {
            LProjectIdentityIcon(
                iconKey = iconKey,
                modifier = Modifier.iconSize(IconSize.XL),
            )
        }
    }
}

/** First user-perceived grapheme, with only single-grapheme invariant uppercase mappings applied. */
internal fun projectMonogram(
    name: String,
    useIntlSegmenter: Boolean = true,
): String? {
    val trimmed = name.trim()
    if (trimmed.isEmpty()) return null
    val first = firstUserPerceivedGrapheme(trimmed, useIntlSegmenter)
    val upper = first.uppercase()
    return if (firstUserPerceivedGrapheme(upper, useIntlSegmenter) == upper) upper else first
}

internal fun projectIdentityTone(
    accentKey: ProjectAccentKey,
    palette: ProjectIdentityPalette,
): Color = when (accentKey) {
    ProjectAccentKey.INK -> palette.ink
    ProjectAccentKey.COBALT -> palette.cobalt
    ProjectAccentKey.JADE -> palette.jade
    ProjectAccentKey.AMBER -> palette.amber
    ProjectAccentKey.CORAL -> palette.coral
    ProjectAccentKey.VIOLET -> palette.violet
}

private fun firstUserPerceivedGrapheme(
    value: String,
    useIntlSegmenter: Boolean,
): String {
    if (!useIntlSegmenter) return firstGraphemeFallback(value)
    return runCatching {
        val options = js("({ granularity: 'grapheme' })")
        val segments = Intl.Segmenter(options = options).segment(value)
        js("Array.from(segments)[0].segment") as String
    }.getOrElse {
        firstGraphemeFallback(value)
    }
}

/**
 * A dependency-free implementation of the UAX #29 extended-grapheme boundary rules.
 *
 * Unicode property escapes supply the versioned Extend / SpacingMark / emoji tables already built
 * into the JavaScript engine. Hangul, Prepend, RI pairing, emoji ZWJ, and Indic-conjunct rules need
 * context, so they are applied explicitly while walking code points. This path is test-selectable;
 * Chrome's `Intl.Segmenter` must not be allowed to hide a fallback regression.
 */
private fun firstGraphemeFallback(value: String): String {
    val points = codePointStrings(value)
    if (points.isEmpty()) return ""
    var end = 1
    while (end < points.size && !hasGraphemeBreak(points, end)) {
        end += 1
    }
    return points.take(end).joinToString("")
}

private fun hasGraphemeBreak(points: List<String>, index: Int): Boolean {
    val previous = graphemeClass(points[index - 1])
    val current = graphemeClass(points[index])

    if (previous == GraphemeClass.CR && current == GraphemeClass.LF) return false
    if (previous.isBreakControl || current.isBreakControl) return true
    if (hangulJoins(previous, current)) return false
    if (current == GraphemeClass.EXTEND || current == GraphemeClass.ZWJ) return false
    if (current == GraphemeClass.SPACING_MARK) return false
    if (previous == GraphemeClass.PREPEND) return false
    if (joinsIndicConjunct(points, index)) return false
    if (joinsEmojiZwjSequence(points, index)) return false
    if (previous == GraphemeClass.REGIONAL_INDICATOR && current == GraphemeClass.REGIONAL_INDICATOR) {
        var precedingRegionalIndicators = 0
        var scan = index - 1
        while (scan >= 0 && graphemeClass(points[scan]) == GraphemeClass.REGIONAL_INDICATOR) {
            precedingRegionalIndicators += 1
            scan -= 1
        }
        return precedingRegionalIndicators % 2 == 0
    }
    return true
}

private fun hangulJoins(previous: GraphemeClass, current: GraphemeClass): Boolean =
    (previous == GraphemeClass.L && current in setOf(GraphemeClass.L, GraphemeClass.V, GraphemeClass.LV, GraphemeClass.LVT)) ||
        (previous in setOf(GraphemeClass.LV, GraphemeClass.V) && current in setOf(GraphemeClass.V, GraphemeClass.T)) ||
        (previous in setOf(GraphemeClass.LVT, GraphemeClass.T) && current == GraphemeClass.T)

private fun joinsEmojiZwjSequence(points: List<String>, index: Int): Boolean {
    if (!matchesUnicodeProperty(points[index], "Extended_Pictographic")) return false
    if (graphemeClass(points[index - 1]) != GraphemeClass.ZWJ) return false
    var scan = index - 2
    while (scan >= 0 && graphemeClass(points[scan]) == GraphemeClass.EXTEND) scan -= 1
    return scan >= 0 && matchesUnicodeProperty(points[scan], "Extended_Pictographic")
}

private fun joinsIndicConjunct(points: List<String>, index: Int): Boolean {
    if (!isIndicConsonant(points[index])) return false
    var scan = index - 1
    var sawLinker = false
    while (scan >= 0) {
        val codePoint = codePointOf(points[scan])
        if (codePoint in INDIC_LINKERS) {
            sawLinker = true
            scan -= 1
            continue
        }
        val type = graphemeClass(points[scan])
        if (type == GraphemeClass.EXTEND || type == GraphemeClass.ZWJ) {
            scan -= 1
            continue
        }
        return sawLinker && isIndicConsonant(points[scan])
    }
    return false
}

private fun graphemeClass(value: String): GraphemeClass {
    val codePoint = codePointOf(value)
    return when {
        codePoint == CARRIAGE_RETURN -> GraphemeClass.CR
        codePoint == LINE_FEED -> GraphemeClass.LF
        codePoint == ZERO_WIDTH_JOINER -> GraphemeClass.ZWJ
        matchesUnicodeProperty(value, "Grapheme_Extend") ||
            matchesUnicodeProperty(value, "Emoji_Modifier") -> GraphemeClass.EXTEND
        matchesUnicodeProperty(value, "Spacing_Mark") -> GraphemeClass.SPACING_MARK
        isPrepend(codePoint) -> GraphemeClass.PREPEND
        codePoint in 0x1100..0x115F || codePoint in 0xA960..0xA97C -> GraphemeClass.L
        codePoint in 0x1160..0x11A7 || codePoint in 0xD7B0..0xD7C6 -> GraphemeClass.V
        codePoint in 0x11A8..0x11FF || codePoint in 0xD7CB..0xD7FB -> GraphemeClass.T
        codePoint in 0xAC00..0xD7A3 ->
            if ((codePoint - 0xAC00) % 28 == 0) GraphemeClass.LV else GraphemeClass.LVT
        matchesUnicodeProperty(value, "Regional_Indicator") -> GraphemeClass.REGIONAL_INDICATOR
        matchesUnicodeProperty(value, "Control") ||
            matchesUnicodeProperty(value, "Format") ||
            matchesUnicodeProperty(value, "Line_Separator") ||
            matchesUnicodeProperty(value, "Paragraph_Separator") -> GraphemeClass.CONTROL
        else -> GraphemeClass.OTHER
    }
}

private fun isPrepend(codePoint: Int): Boolean =
    codePoint in 0x0600..0x0605 ||
        codePoint == 0x06DD ||
        codePoint == 0x070F ||
        codePoint in 0x0890..0x0891 ||
        codePoint == 0x08E2 ||
        codePoint == 0x0D4E ||
        codePoint == 0x110BD ||
        codePoint == 0x110CD ||
        codePoint in 0x111C2..0x111C3 ||
        codePoint == 0x1193F ||
        codePoint == 0x11941 ||
        codePoint == 0x11A3A ||
        codePoint in 0x11A84..0x11A89 ||
        codePoint == 0x11D46 ||
        codePoint == 0x11F02 ||
        codePoint in 0x13430..0x1343F

private fun isIndicConsonant(value: String): Boolean {
    val codePoint = codePointOf(value)
    val inIndicBlock = codePoint in 0x0900..0x0DFF ||
        codePoint in 0x0F00..0x109F ||
        codePoint in 0x1700..0x17FF ||
        codePoint in 0x1A00..0x1CFF ||
        codePoint in 0xA800..0xABFF ||
        codePoint in 0x11000..0x11FFF
    return inIndicBlock && matchesUnicodeProperty(value, "Letter")
}

private fun codePointStrings(value: String): List<String> {
    val points: dynamic = js("Array.from(value)")
    return List(points.length as Int) { index -> points[index] as String }
}

private fun codePointOf(value: String): Int = value.asDynamic().codePointAt(0) as Int

private fun matchesUnicodeProperty(value: String, property: String): Boolean =
    runCatching {
        js("new RegExp('^\\\\p{' + property + '}$', 'u').test(value)") as Boolean
    }.getOrDefault(false)

private enum class GraphemeClass {
    CR,
    LF,
    CONTROL,
    EXTEND,
    ZWJ,
    REGIONAL_INDICATOR,
    PREPEND,
    SPACING_MARK,
    L,
    V,
    T,
    LV,
    LVT,
    OTHER,
    ;

    val isBreakControl: Boolean get() = this == CR || this == LF || this == CONTROL
}

private val INDIC_LINKERS = setOf(
    0x094D, 0x09CD, 0x0A4D, 0x0ACD, 0x0B4D, 0x0BCD, 0x0C4D, 0x0CCD, 0x0D3B,
    0x0D3C, 0x0D4D, 0x0DCA, 0x0E3A, 0x0F84, 0x1039, 0x103A, 0x1714, 0x1734,
    0x17D2, 0x1A60, 0x1B44, 0x1BAA, 0x1BAB, 0x1BF2, 0x1BF3, 0x2D7F, 0xA806,
    0xA82C, 0xA8C4, 0xA953, 0xA9C0, 0xAAF6, 0xABED, 0x10A3F, 0x11046, 0x1107F,
    0x110B9, 0x11133, 0x11134, 0x111C0, 0x11235, 0x112EA, 0x1134D, 0x11442,
    0x114C2, 0x115BF, 0x1163F, 0x116B6, 0x1172B, 0x11839, 0x1193D, 0x119E0,
    0x11A34, 0x11A47, 0x11A99, 0x11C3F, 0x11D44, 0x11D45, 0x11D97, 0x11F41,
)

private const val CARRIAGE_RETURN = 0x000D
private const val LINE_FEED = 0x000A
private const val ZERO_WIDTH_JOINER = 0x200D

private external object Intl {
    class Segmenter(
        locales: dynamic = definedExternally,
        options: dynamic = definedExternally,
    ) {
        fun segment(value: String): dynamic
    }
}
